import { useEffect, useId, useRef } from 'react';
import type { Btc15LiveTick } from '../../lib/btc15Types';
import { formatUsd } from '../../lib/format';
import styles from './Btc15LiveRound.module.css';

const WIDTH = 640;
const HEIGHT = 240;
const PAD_TOP = 16;
const PAD_BOTTOM = 16;
/** Rolling window shown in "live" mode, and the gap kept ahead of "now". */
const LIVE_WINDOW_MS = 120_000;
const LIVE_LEAD_MS = 6_000;
/** How long the line's tip takes to glide to a newly polled price. */
const HEAD_EASE_MS = 900;
/** Time constant for the vertical scale settling to a new range. */
const SCALE_TAU_MS = 350;
const Y_TICKS = 4;

export type ChartMode = 'live' | 'round';

interface Props {
  path: Btc15LiveTick[];
  target: number | null;
  startMs: number;
  closeMs: number;
  mode: ChartMode;
  /** Plain-language summary for screen readers. */
  summary: string;
}

const easeOut = (t: number) => 1 - (1 - t) ** 3;

/**
 * Monotone cubic curve through the points (Fritsch–Carlson), as SVG cubic
 * Béziers. Unlike a Catmull-Rom spline it never overshoots between points,
 * which matters here: sparse 1-minute closes sit next to closely spaced
 * live prices, and an overshooting curve draws hooks that never happened.
 */
function smoothPath(points: Array<[number, number]>): string {
  const n = points.length;
  if (n === 0) return '';
  const f = (v: number) => v.toFixed(1);
  if (n === 1) return `M${f(points[0][0])},${f(points[0][1])}`;
  const dx: number[] = [];
  const slope: number[] = [];
  for (let i = 0; i < n - 1; i++) {
    dx[i] = points[i + 1][0] - points[i][0] || 1e-6;
    slope[i] = (points[i + 1][1] - points[i][1]) / dx[i];
  }
  const m: number[] = new Array(n);
  m[0] = slope[0];
  m[n - 1] = slope[n - 2];
  for (let i = 1; i < n - 1; i++) {
    if (slope[i - 1] * slope[i] <= 0) {
      m[i] = 0;
    } else {
      const w1 = 2 * dx[i] + dx[i - 1];
      const w2 = dx[i] + 2 * dx[i - 1];
      m[i] = (w1 + w2) / (w1 / slope[i - 1] + w2 / slope[i]);
    }
  }
  let d = `M${f(points[0][0])},${f(points[0][1])}`;
  for (let i = 0; i < n - 1; i++) {
    const [x0, y0] = points[i];
    const [x1, y1] = points[i + 1];
    const h = dx[i] / 3;
    d += ` C${f(x0 + h)},${f(y0 + m[i] * h)} ${f(x1 - h)},${f(y1 - m[i + 1] * h)} ${f(x1)},${f(y1)}`;
  }
  return d;
}

/**
 * The live round chart. Drawn imperatively on every animation frame (not
 * through React state) so it can scroll continuously with the clock and
 * glide between polled prices: the newest price eases in, the time axis
 * moves smoothly, and the vertical scale settles instead of jumping. With
 * reduced motion it simply redraws when new data arrives.
 */
export function LiveRoundChart({ path, target, startMs, closeMs, mode, summary }: Props) {
  const titleId = useId();
  const descId = useId();
  const gradientId = useId();

  const lineRef = useRef<SVGPathElement>(null);
  const areaRef = useRef<SVGPathElement>(null);
  const targetLineRef = useRef<SVGLineElement>(null);
  const dotRef = useRef<HTMLSpanElement>(null);
  const tagRef = useRef<HTMLSpanElement>(null);
  const yLabelRefs = useRef<Array<HTMLSpanElement | null>>([]);
  const gridRefs = useRef<Array<SVGLineElement | null>>([]);
  const startLabelRef = useRef<HTMLSpanElement>(null);
  const endLabelRef = useRef<HTMLSpanElement>(null);

  // Latest props for the animation loop, plus the tip's glide state.
  const latest = useRef({ path, target, startMs, closeMs, mode });
  latest.current = { path, target, startMs, closeMs, mode };
  const head = useRef<{ from: number; to: number; at: number } | null>(null);
  const domain = useRef<{ min: number; max: number } | null>(null);

  // A new polled price starts a glide from wherever the tip is drawn now.
  const lastPrice = path.length ? path[path.length - 1].price : null;
  useEffect(() => {
    if (lastPrice === null) return;
    const now = performance.now();
    const h = head.current;
    if (!h) {
      head.current = { from: lastPrice, to: lastPrice, at: now };
      return;
    }
    const t = Math.min(1, (now - h.at) / HEAD_EASE_MS);
    const current = h.from + (h.to - h.from) * easeOut(t);
    head.current = { from: current, to: lastPrice, at: now };
  }, [lastPrice]);

  // Snap the scale when switching between the live window and the round.
  useEffect(() => {
    domain.current = null;
  }, [mode, startMs]);

  useEffect(() => {
    const reduced =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let frame = 0;
    let lastFrame = performance.now();
    const timeFormat = new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });

    const draw = () => {
      const {
        path: pts,
        target: tgt,
        startMs: s0,
        closeMs: s1,
        mode: m,
      } = latest.current;
      const perfNow = performance.now();
      const dt = perfNow - lastFrame;
      lastFrame = perfNow;
      if (pts.length === 0) return;

      const lastTick = pts[pts.length - 1];
      const wall = reduced ? lastTick.t : Math.min(Date.now(), s1);
      const h = head.current;
      const headPrice =
        h && !reduced
          ? h.from +
            (h.to - h.from) * easeOut(Math.min(1, (perfNow - h.at) / HEAD_EASE_MS))
          : lastTick.price;

      const x0 = m === 'live' ? wall - LIVE_WINDOW_MS : s0;
      const x1 = m === 'live' ? wall + LIVE_LEAD_MS : s1;

      // Visible points (plus one just before the window so the line enters
      // from the edge), with the tip drawn at "now" at its gliding price.
      const visible: Btc15LiveTick[] = [];
      for (let i = 0; i < pts.length - 1; i++) {
        const next = pts[i + 1];
        if (next.t >= x0) visible.push(pts[i]);
      }
      visible.push({ t: Math.max(wall, lastTick.t), price: headPrice });

      // Vertical range from what's visible and the target, eased.
      let lo = Infinity;
      let hi = -Infinity;
      for (const p of visible) {
        if (p.t < x0) continue;
        lo = Math.min(lo, p.price);
        hi = Math.max(hi, p.price);
      }
      if (tgt !== null) {
        lo = Math.min(lo, tgt);
        hi = Math.max(hi, tgt);
      }
      if (!Number.isFinite(lo)) {
        lo = headPrice;
        hi = headPrice;
      }
      const pad = Math.max((hi - lo) * 0.18, 3);
      const wantMin = lo - pad;
      const wantMax = hi + pad;
      const d = domain.current;
      if (!d || reduced) {
        domain.current = { min: wantMin, max: wantMax };
      } else {
        const k = 1 - Math.exp(-dt / SCALE_TAU_MS);
        d.min += (wantMin - d.min) * k;
        d.max += (wantMax - d.max) * k;
      }
      const { min, max } = domain.current!;

      const xOf = (t: number) => ((t - x0) / (x1 - x0)) * WIDTH;
      const yOf = (price: number) =>
        PAD_TOP + (1 - (price - min) / (max - min)) * (HEIGHT - PAD_TOP - PAD_BOTTOM);

      const xy = visible.map((p) => [xOf(p.t), yOf(p.price)] as [number, number]);
      const line = smoothPath(xy);
      const tip = xy[xy.length - 1];
      lineRef.current?.setAttribute('d', line);
      areaRef.current?.setAttribute(
        'd',
        `${line} L${tip[0].toFixed(1)},${HEIGHT} L${xy[0][0].toFixed(1)},${HEIGHT} Z`,
      );
      if (dotRef.current) {
        dotRef.current.style.left = `${(tip[0] / WIDTH) * 100}%`;
        dotRef.current.style.top = `${(tip[1] / HEIGHT) * 100}%`;
      }
      if (tgt !== null) {
        const ty = yOf(tgt);
        targetLineRef.current?.setAttribute('y1', ty.toFixed(1));
        targetLineRef.current?.setAttribute('y2', ty.toFixed(1));
        if (tagRef.current) tagRef.current.style.top = `${(ty / HEIGHT) * 100}%`;
      }
      for (let i = 0; i < Y_TICKS; i++) {
        const value = min + pad * 0.5 + ((max - min - pad) * i) / (Y_TICKS - 1);
        const y = yOf(value);
        const label = yLabelRefs.current[i];
        if (label) {
          label.style.top = `${(y / HEIGHT) * 100}%`;
          const text = Math.round(value).toLocaleString('en-US');
          if (label.textContent !== text) label.textContent = text;
        }
        gridRefs.current[i]?.setAttribute('y1', y.toFixed(1));
        gridRefs.current[i]?.setAttribute('y2', y.toFixed(1));
      }
      const startText = m === 'live' ? '2 min ago' : timeFormat.format(s0);
      const endText = m === 'live' ? 'Now' : timeFormat.format(s1);
      if (startLabelRef.current && startLabelRef.current.textContent !== startText)
        startLabelRef.current.textContent = startText;
      if (endLabelRef.current && endLabelRef.current.textContent !== endText)
        endLabelRef.current.textContent = endText;
    };

    if (reduced) {
      draw();
      return;
    }
    const loop = () => {
      draw();
      frame = requestAnimationFrame(loop);
    };
    frame = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(frame);
    // Redraw on data changes too, for the reduced-motion path.
  }, [path, target, mode, startMs, closeMs]);

  return (
    <figure className={styles.figure}>
      <div className={styles.plot}>
        <div className={styles.plotArea}>
          <svg
            role="img"
            aria-labelledby={titleId}
            aria-describedby={descId}
            viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
            preserveAspectRatio="none"
            className={styles.svg}
          >
            <title id={titleId}>BTC price this round against the price to beat</title>
            <desc id={descId}>{summary}</desc>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" className={styles.areaTop} />
                <stop offset="100%" className={styles.areaBottom} />
              </linearGradient>
            </defs>
            {Array.from({ length: Y_TICKS }, (_, i) => (
              <line
                key={i}
                ref={(el) => {
                  gridRefs.current[i] = el;
                }}
                className={styles.grid}
                x1={0}
                x2={WIDTH}
              />
            ))}
            <path ref={areaRef} fill={`url(#${gradientId})`} stroke="none" />
            {target !== null && (
              <line ref={targetLineRef} className={styles.targetLine} x1={0} x2={WIDTH} />
            )}
            <path ref={lineRef} className={styles.priceLine} fill="none" />
          </svg>
          <span ref={dotRef} className={styles.dot} aria-hidden="true" />
          {target !== null && (
            <span
              ref={tagRef}
              // Sits at the far end from the moving tip so it never covers it.
              className={`${styles.targetTag} ${mode === 'live' ? styles.targetTagStart : ''}`}
              aria-hidden="true"
            >
              Target {formatUsd(target)}
            </span>
          )}
        </div>
        <div className={styles.yAxis} aria-hidden="true">
          {Array.from({ length: Y_TICKS }, (_, i) => (
            <span
              key={i}
              ref={(el) => {
                yLabelRefs.current[i] = el;
              }}
            />
          ))}
        </div>
      </div>
      <div className={styles.xAxis} aria-hidden="true">
        <span ref={startLabelRef} />
        <span ref={endLabelRef} />
      </div>
    </figure>
  );
}
